import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Briefcase, Award, Building2,
  Globe, Lock, Users, Bell, CheckCircle, Eye,
  FileText, Linkedin, Clock, Info, UserCheck, AlertTriangle
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import ProfileCard from '../../components/consultant/ProfileCard';

interface ProfileData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  company: string;
  title: string;
  country_region: string;
  experience_years: string;
  workday_modules: string;
  certifications: string;
  experience_text: string;
  linkedin_url: string;
  availability_status: string;
  hourly_rate: string;
  profile_complete: boolean;
  project_end_date?: string;
  resume_url?: string;
  photo_url?: string;
  photo_position_x?: number;
  photo_position_y?: number;
  profile_visibility?: string;
  show_contact_info?: boolean;
  allow_direct_messages?: boolean;
}

export default function ConsultantProfilePublic() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, setTheme, setThemeTransient } = useTheme();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoadingLogo, setIsLoadingLogo] = useState(true);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [connectionsCount, setConnectionsCount] = useState(0);
  const [followersCount, setFollowersCount] = useState(0);
  const [showProfileCard, setShowProfileCard] = useState(false);

  useEffect(() => {
    setThemeTransient('light');
  }, [setThemeTransient]);

  useEffect(() => {
    if (user && id) {
      fetchProfile();
      checkConnection();
      fetchCounts();
    }
    fetchLogo();
  }, [user, id]);

  useEffect(() => {
    if (!id) return;
    const channel = supabase
      .channel(`public-profile-${id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'consultant_profiles',
        filter: `id=eq.${id}`,
      }, () => { fetchProfile(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [id]);

  const fetchLogo = async () => {
    try {
      setIsLoadingLogo(true);
      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);
      const { data } = await supabase.from('settings').select('value').eq('key', 'logo_url').maybeSingle();
      if (data?.value) {
        const img = new Image();
        img.onload = () => { setLogoUrl(data.value); setIsLoadingLogo(false); };
        img.onerror = () => setIsLoadingLogo(false);
        img.src = data.value;
      } else {
        setIsLoadingLogo(false);
      }
    } catch {
      setIsLoadingLogo(false);
    }
  };

  const fetchProfile = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('consultant_profiles')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      if (data) {
        setProfile(data);
        if (data.photo_url) {
          if (data.photo_url.startsWith('http')) {
            setPhotoUrl(data.photo_url);
          } else {
            const { data: urlData } = supabase.storage.from('profile-photos').getPublicUrl(data.photo_url);
            setPhotoUrl(urlData.publicUrl);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkConnection = async () => {
    if (!user?.id || !id) return;
    try {
      const { data } = await supabase
        .from('community_connections')
        .select('id')
        .or(`and(requester_id.eq.${user.id},addressee_id.eq.${id}),and(requester_id.eq.${id},addressee_id.eq.${user.id})`)
        .eq('status', 'accepted')
        .maybeSingle();
      setIsConnected(!!data);
    } catch {
      setIsConnected(false);
    }
  };

  const fetchCounts = async () => {
    if (!id) return;
    try {
      const [connResult, followResult] = await Promise.all([
        supabase
          .from('community_connections')
          .select('id', { count: 'exact', head: true })
          .or(`requester_id.eq.${id},addressee_id.eq.${id}`)
          .eq('status', 'accepted'),
        supabase
          .from('community_followers')
          .select('id', { count: 'exact', head: true })
          .eq('following_id', id),
      ]);
      if (connResult.count !== null) setConnectionsCount(connResult.count);
      if (followResult.count !== null) setFollowersCount(followResult.count);
    } catch {}
  };

  const isDark = theme === 'dark';

  const availabilityConfig = {
    available: { label: 'Available', dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    on_project: { label: 'On Project', dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
    unavailable: { label: 'Unavailable', dot: 'bg-red-500', badge: 'bg-red-100 text-red-700 border-red-200' },
  };

  const avail = availabilityConfig[(profile?.availability_status || 'available') as keyof typeof availabilityConfig];

  const modules = profile?.workday_modules
    ? profile.workday_modules.split(',').map((m) => m.trim()).filter(Boolean)
    : [];

  const certs = profile?.certifications
    ? profile.certifications.split(',').map((c) => c.trim()).filter(Boolean)
    : [];

  const initials = `${profile?.first_name?.[0] || ''}${profile?.last_name?.[0] || ''}`.toUpperCase();

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Profile not found</p>
          <button onClick={() => navigate('/consultant/community')} className="mt-4 text-[#40E0D0] hover:underline">
            Back to Community
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <nav className="fixed w-full z-50 bg-black backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="text-white -mt-4 -mb-4 cursor-default select-none">
              {isLoadingLogo ? (
                <div className="h-32 md:h-28 w-[200px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]" />
                </div>
              ) : logoUrl ? (
                <img src="/1.png" alt="Logo" className="h-40 w-auto object-contain" />
              ) : (
                <span className="text-2xl font-bold text-white">Perplexity</span>
              )}
            </div>
            <button
              onClick={() => navigate('/consultant/community')}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Community
            </button>
          </div>
        </div>
      </nav>

      <div className="pt-24">
        <div className="max-w-3xl mx-auto px-4 py-8">

          {!isConnected && (
            <div className={`mb-6 rounded-xl border px-5 py-4 flex items-start gap-3 ${
              isDark ? 'bg-amber-900/20 border-amber-700/40' : 'bg-amber-50 border-amber-200'
            }`}>
              <Lock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className={`text-sm font-semibold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                  Limited Profile View
                </p>
                <p className={`text-sm mt-0.5 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                  Connect with {profile.first_name} to view their full profile, experience, and contact details.
                </p>
              </div>
            </div>
          )}

          <div className={`rounded-2xl shadow-lg overflow-hidden border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="h-32 bg-gradient-to-r from-[#40E0D0] to-[#2bbfb0] relative">
              <div className="absolute -bottom-14 left-8">
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={`${profile.first_name} ${profile.last_name}`}
                    className={`w-28 h-28 rounded-full object-cover border-4 shadow-lg ${isDark ? 'border-gray-800' : 'border-white'}`}
                    style={{
                      objectPosition: `${profile.photo_position_x ?? 50}% ${profile.photo_position_y ?? 50}%`
                    }}
                  />
                ) : (
                  <div className={`w-28 h-28 rounded-full border-4 shadow-lg bg-[#40E0D0] flex items-center justify-center ${isDark ? 'border-gray-800' : 'border-white'}`}>
                    <span className="text-white text-3xl font-bold">{initials || '?'}</span>
                  </div>
                )}
              </div>
              {isConnected && (
                <div className="absolute top-4 right-4">
                  <button
                    onClick={() => setShowProfileCard(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white text-sm font-semibold rounded-full backdrop-blur-sm border border-white/30 transition-all"
                  >
                    <Eye className="w-4 h-4" />
                    Profile Card
                  </button>
                </div>
              )}
            </div>

            <div className="pt-20 px-8 pb-8">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {profile.first_name} {profile.last_name}
                  </h1>
                  {profile.title && (
                    <p className={`text-base font-medium mt-0.5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {profile.title}
                    </p>
                  )}
                </div>
                <span className={`text-sm font-semibold px-3 py-1.5 rounded-full border flex items-center gap-2 ${avail.badge}`}>
                  <span className={`w-2 h-2 rounded-full ${avail.dot}`} />
                  {avail.label}
                </span>
              </div>

              <div className={`flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {profile.company && (
                  <span className="flex items-center gap-1.5">
                    <Building2 className="w-4 h-4" />
                    {profile.company}
                  </span>
                )}
                {profile.country_region && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" />
                    {profile.country_region}
                  </span>
                )}
                {profile.experience_years && (
                  <span className="flex items-center gap-1.5">
                    <Briefcase className="w-4 h-4" />
                    {profile.experience_years} years Workday experience
                  </span>
                )}
                {isConnected && profile.linkedin_url && (
                  <a
                    href={profile.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-blue-500 hover:text-blue-600 transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                    LinkedIn
                  </a>
                )}
              </div>

              <div className={`flex items-center gap-6 mt-4 pt-4 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-100 text-gray-500'}`}>
                <span className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4" />
                  <strong className={isDark ? 'text-white' : 'text-gray-900'}>{connectionsCount}</strong> connections
                </span>
                <span className="flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  <strong className={isDark ? 'text-white' : 'text-gray-900'}>{followersCount}</strong> followers
                </span>
                {isConnected && (
                  <span className="flex items-center gap-2 text-emerald-600 text-xs font-medium">
                    <CheckCircle className="w-4 h-4" />
                    Connected
                  </span>
                )}
              </div>

              {modules.length > 0 && (
                <div className={`mt-6 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <h3 className={`text-sm font-semibold uppercase tracking-wide mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Workday Modules
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {modules.map((mod, i) => (
                      <span
                        key={i}
                        className={`text-sm px-3 py-1.5 rounded-full font-medium border ${
                          isDark
                            ? 'bg-[#40E0D0]/10 text-[#40E0D0] border-[#40E0D0]/30'
                            : 'bg-[#40E0D0]/10 text-[#1a9e90] border-[#40E0D0]/40'
                        }`}
                      >
                        {mod}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {certs.length > 0 && (
                <div className={`mt-6 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <h3 className={`text-sm font-semibold uppercase tracking-wide mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Certifications
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {certs.map((cert, i) => (
                      <span
                        key={i}
                        className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full font-medium bg-amber-50 text-amber-700 border border-amber-200"
                      >
                        <Award className="w-3.5 h-3.5" />
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {isConnected && profile.experience_text && (
                <div className={`mt-6 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <h3 className={`text-sm font-semibold uppercase tracking-wide mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Experience
                  </h3>
                  <p className={`text-sm leading-relaxed whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {profile.experience_text}
                  </p>
                </div>
              )}

              {isConnected && profile.show_contact_info && (
                <div className={`mt-6 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <h3 className={`text-sm font-semibold uppercase tracking-wide mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Contact
                  </h3>
                  <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    <span className="text-[#40E0D0]">@</span> {profile.email}
                  </div>
                  <p className={`text-xs mt-1 flex items-center gap-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                    <Lock className="w-3 h-3" /> Phone is always private
                  </p>
                </div>
              )}

              {isConnected && profile.availability_status === 'available' && profile.hourly_rate && (
                <div className={`mt-5 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Clock className="w-4 h-4 text-[#40E0D0]" />
                    <span className="font-medium">Hourly Rate:</span>
                    <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>${profile.hourly_rate}/hr</span>
                  </div>
                </div>
              )}

              {!isConnected && (
                <div className={`mt-6 pt-5 border-t flex flex-col items-center py-6 gap-3 ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <Lock className={`w-10 h-10 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                  <p className={`text-sm text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Full profile details are only visible to connections.
                    <br />Connect with {profile.first_name} through the Community to view more.
                  </p>
                  <button
                    onClick={() => navigate('/consultant/community')}
                    className="mt-2 px-5 py-2 bg-[#40E0D0] hover:bg-[#35c5b5] text-white text-sm font-semibold rounded-full transition-colors"
                  >
                    Go to Community
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showProfileCard && profile && isConnected && (
        <ProfileCard
          profile={profile}
          photoUrl={photoUrl}
          connectionsCount={connectionsCount}
          followersCount={followersCount}
          onClose={() => setShowProfileCard(false)}
          theme={theme}
        />
      )}
    </div>
  );
}
